import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SyncIcon from "@mui/icons-material/Sync";
import {
  useDeleteRisorsaCalDav,
  useGetRisorsaCalDav,
  useSyncRisorsaCalDav,
  useTestRisorsaCalDav,
} from "./Services";
import { RisorsaCalDav } from "./types";
import RisorsaCalDavDialog from "./RisorsaCalDavDialog";

const ListaRisorsaCalDav: React.FC = () => {
  const [globalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [dialogId, setDialogId] = useState<number | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState("");

  const { data: { results = [], count = 0 } = {}, isLoading, isFetching, isError } =
    useGetRisorsaCalDav(pagination, columnFilters, globalFilter, sorting);
  const { mutateAsync: deleteItem, isPending: isDeleting } = useDeleteRisorsaCalDav();
  const { mutateAsync: testItem, isPending: isTesting } = useTestRisorsaCalDav();
  const { mutateAsync: syncItem, isPending: isSyncing } = useSyncRisorsaCalDav();

  const columns = useMemo<MRT_ColumnDef<RisorsaCalDav>[]>(
    () => [
      { accessorKey: "id", header: "Id", size: 50, enableEditing: false },
      { accessorKey: "coddip", header: "CodDip", size: 80 },
      { accessorKey: "caldavUrl", header: "URL CalDAV", size: 360 },
      { accessorKey: "username", header: "Username", size: 160 },
      {
        accessorKey: "enabled",
        header: "Abilitato",
        size: 80,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            color={cell.getValue<boolean>() ? "success" : "default"}
            label={cell.getValue<boolean>() ? "SI" : "NO"}
          />
        ),
      },
      {
        accessorKey: "hasPassword",
        header: "Password",
        size: 80,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            color={cell.getValue<boolean>() ? "success" : "warning"}
            label={cell.getValue<boolean>() ? "Salvata" : "Manca"}
          />
        ),
      },
    ],
    []
  );

  const openEdit = (row: MRT_Row<RisorsaCalDav>) => {
    setDialogId(row.original.id);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setDialogId(undefined);
    setDialogOpen(true);
  };

  const handleDelete = async (row: MRT_Row<RisorsaCalDav>) => {
    if (!row.original.id) return;
    if (window.confirm(`Cancellare configurazione CalDAV per ${row.original.coddip}?`)) {
      await deleteItem(row.original.id);
    }
  };

  const handleTest = async (row: MRT_Row<RisorsaCalDav>) => {
    if (!row.original.id) return;
    try {
      const result = await testItem(row.original.id);
      setSnackbar(result.message || "Connessione CalDAV OK");
    } catch (error: any) {
      setSnackbar(error?.response?.data?.message || "Test CalDAV fallito");
    }
  };

  const handleSync = async (row: MRT_Row<RisorsaCalDav>) => {
    if (!row.original.id) return;
    try {
      const result = await syncItem(row.original.id);
      setSnackbar(
        `Sync completata: ${result.synced ?? 0} eventi, ${result.errors ?? 0} errori`
      );
    } catch (error: any) {
      setSnackbar(error?.response?.data?.message || "Sync CalDAV fallita");
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: results,
    initialState: { density: "compact" },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableEditing: true,
    getRowId: (row) => String(row.id ?? ""),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount: count,
    muiToolbarAlertBannerProps: isError ? { color: "error", children: "Errore caricamento CalDAV" } : undefined,
    renderTopToolbarCustomActions: () => (
      <Button variant="contained" onClick={openCreate}>
        Nuovo
      </Button>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "0.25rem" }}>
        <Tooltip title="Modifica">
          <IconButton onClick={() => openEdit(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Test connessione">
          <IconButton onClick={() => handleTest(row)} disabled={isTesting}>
            <CloudSyncIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sincronizza attività da oggi">
          <IconButton onClick={() => handleSync(row)} disabled={isSyncing}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rimuovi">
          <IconButton color="error" onClick={() => handleDelete(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    state: {
      isLoading,
      isSaving: isDeleting || isTesting || isSyncing,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      columnFilters,
      pagination,
      sorting,
    },
  });

  return (
    <Box sx={{ mt: "60px" }}>
      <MaterialReactTable table={table} />
      {dialogOpen && (
        <RisorsaCalDavDialog
          id={dialogId}
          onClose={() => {
            setDialogOpen(false);
            setDialogId(undefined);
          }}
        />
      )}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        message={snackbar}
        onClose={() => setSnackbar("")}
      />
    </Box>
  );
};

export default ListaRisorsaCalDav;
